/**
 * Real-backend auth provider (Phase 3 cut-over).
 *
 * Talks to the FastAPI backend on `${REACT_APP_BACKEND_URL}/api`:
 *   - GET  /api/me          → hydrates the session on mount and after
 *                              every tab focus.
 *   - POST /api/auth/login  → exchanges email + password for an HttpOnly
 *                              access cookie.
 *   - POST /api/auth/logout → clears the cookies.
 *
 * Token storage: nothing on the JS side. The HttpOnly cookie is the
 * single source of authentication; we never touch sessionStorage or
 * localStorage for tokens (per the security decision: "HttpOnly cookies
 * only").
 *
 * Persona / tenant come from the `/api/me` payload — already resolved
 * server-side, so the frontend never re-derives the matrix.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, ApiError } from "@/lib/api";
import { logger } from "@/lib/logger";
import { AuthContext } from "@/contexts/AuthContext";
import { getPersonasForTenant } from "@/lib/mockData";

// Pure fetcher (no React state) — keeps the effect body free of setters
// the linter would otherwise flag as "state-in-effect".
async function fetchMe() {
  try {
    const me = await api.get("/api/me");
    return { ok: true, user: me };
  } catch (e) {
    if (!(e instanceof ApiError) || e.status !== 401) {
      logger.warn("[auth] /me failed", e);
    }
    return { ok: false, error: e };
  }
}

export function ApiAuthProvider({ children }) {
  const [user, setUser] = useState(null);     // UserPublic | null
  const [hydrated, setHydrated] = useState(false);
  const [error, setError] = useState(null);
  const [sessionStartedAt, setSessionStartedAt] = useState(null);
  const lastFetched = useRef(0);

  const applyMeResult = useCallback((result) => {
    if (result.ok) {
      setUser(result.user);
      setSessionStartedAt((prev) => prev || Date.now());
    } else {
      setUser(null);
    }
    setHydrated(true);
    lastFetched.current = Date.now();
  }, []);

  // On mount: ask the server if we already have a valid session.
  useEffect(() => {
    let cancelled = false;
    fetchMe().then((result) => {
      if (!cancelled) applyMeResult(result);
    });
    return () => { cancelled = true; };
  }, [applyMeResult]);

  const refetch = useCallback(async () => {
    const result = await fetchMe();
    applyMeResult(result);
  }, [applyMeResult]);

  const signIn = useCallback(async (email, password) => {
    setError(null);
    try {
      const { user: loggedIn } = await api.post("/api/auth/login", { email, password });
      setUser(loggedIn);
      setSessionStartedAt(Date.now());
      return { ok: true, user: loggedIn };
    } catch (e) {
      const msg = e instanceof ApiError
        ? (e.status === 429 ? "Too many attempts — try again in 15 minutes." : e.message)
        : "Network error";
      setError({ status: e?.status ?? 0, message: msg });
      return { ok: false, status: e?.status ?? 0, message: msg };
    }
  }, []);

  const signOut = useCallback(async () => {
    try { await api.post("/api/auth/logout"); } catch { /* ignore */ }
    setUser(null);
    setSessionStartedAt(null);
  }, []);

  // Persona switching — re-issues a JWT for a different persona on the
  // SAME tenant. Used by the gold "Role" pill in the header so a
  // presenter can swap CAIO → Auditor → Exec Viewer without signing out.
  // Hardened server-side: same-tenant only, target must be active.
  const switchRole = useCallback(async (personaId) => {
    try {
      // Look up the persona's seeded email from the static registry
      // (the registry mirrors the seed-data users 1:1).
      const tenantId = user?.tenant?.tenant_id || user?.tenant_id;
      const persona = getPersonasForTenant(tenantId).find((p) => p.id === personaId);
      if (!persona?.email) {
        logger.warn(`[auth] switchRole(${personaId}) — no email in persona registry`);
        return;
      }
      const result = await api.post("/api/auth/switch-persona", { email: persona.email });
      if (result?.user) {
        setUser(result.user);
        setSessionStartedAt(Date.now());
        logger.info(`[auth] switched to persona ${personaId} (${persona.email})`);
      }
    } catch (err) {
      logger.error(`[auth] switchRole(${personaId}) failed`, err);
    }
  }, [user]);

  const extendSession = useCallback(() => {
    setSessionStartedAt(Date.now());
  }, []);

  const value = useMemo(() => {
    // Adapt the UserPublic payload to the legacy shape every consumer
    // already speaks. We mix in the static persona registry record
    // (UI display metadata only — roleCode, initials, role title) so
    // the shell chrome (Header, RoleSwitcher, UserMenu) doesn't have
    // to special-case API mode.
    const personaRecord = user
      ? getPersonasForTenant(user.tenant?.tenant_id).find((p) => p.id === user.persona_id)
      : null;
    const initials = personaRecord?.initials
      || (user?.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

    const adapted = user
      ? {
          id: user.persona_id,
          name: user.name,
          email: user.email,
          role: personaRecord?.role || user.persona_id,
          roleCode: personaRecord?.roleCode || user.persona_id.toUpperCase(),
          initials,
          department: user.department,
          color: personaRecord?.color || "tralenex",
          permissions: user.permissions || [],
          surfaces: user.surfaces || [],
          isReadOnly: user.is_read_only,
        }
      : null;

    const tenant = user
      ? {
          id: user.tenant.tenant_id,
          name: user.tenant.label,
          industry: user.tenant.industry,
          region: user.tenant.region,
          mode: user.tenant.mode,
        }
      : null;

    return {
      user: adapted,
      tenant,
      sessionStartedAt,
      hydrated,
      isAuthenticated: !!adapted,
      authMode: "api",
      error,
      signIn,
      signOut,
      switchRole,
      extendSession,
      refetch,
    };
  }, [user, sessionStartedAt, hydrated, error, signIn, signOut, switchRole, extendSession, refetch]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
